import moment from 'moment';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { CycleTemplate } from '@treams/treams-analytics';
import { useLocalize } from '@treams/treams-hooks';
import { CycleTemplateInterval } from '@treams/treams-network';
import { Paragraph, UnderlineLink } from '@treams/treams-ui';

export function DetailsList({
	items,
}: {
	items: {
		icon?: ReactNode;
		title: string;
		description: string | ReactNode;
		onClick?: () => void;
	}[];
}) {
	return (
		<Box>
			{items.map((item) => (
				<Flex key={item.title} justifyContent="space-between" my={1}>
					<Flex alignItems="center">
						{item.icon && <Box mr={2}>{item.icon}</Box>}
						<Paragraph color="antracite">{item.title}</Paragraph>
					</Flex>
					<Box>
						<Paragraph>
							{item.onClick ? (
								<UnderlineLink
									color="antracite"
									onClick={(event) => {
										event.preventDefault();
										item.onClick?.();
									}}
								>
									{item.description}
								</UnderlineLink>
							) : (
								item.description
							)}
						</Paragraph>
					</Box>
				</Flex>
			))}
		</Box>
	);
}

export function CycleDetails({
	cycleTemplateID,
	cycleTemplate,
	employeeCount,
	startDate,
	endDate,
	onClickCycleTemplate,
}: {
	cycleTemplateID: string;
	cycleTemplate: CycleTemplate;
	employeeCount: number;
	startDate?: Date;
	endDate?: Date;
	onClickCycleTemplate: (cycleTemplateID: string) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const isActive =
		cycleTemplate.interval === CycleTemplateInterval.Year
			? // For year cycles, the cycle is active if the current date is between the start and end date
				moment().isBetween(startDate, endDate)
			: // For other cycles, the cycle is continuously active
				true;

	const successor = cycleTemplate.successor;
	const successorTitle = successor ? (localize(successor.title)?.text ?? '') : '';
	const isSameCycleSuccessor = successor?.cycleTemplateID === cycleTemplateID;

	return (
		<DetailsList
			items={[
				{
					title: t('analytics.cycles.details.status'),
					description: isActive
						? t('analytics.cycles.details.values.active')
						: t('analytics.cycles.details.values.inactive'),
				},
				{
					title: t('analytics.cycles.details.employees'),
					description: String(employeeCount),
				},
				{
					title: t('analytics.cycles.details.duration'),
					description: t('analytics.cycles.details.values.duration', {
						months: cycleTemplate.duration,
					}),
				},
				{
					title: t('analytics.cycles.details.startDate'),
					description:
						cycleTemplate.interval === CycleTemplateInterval.Year
							? moment(startDate).startOf('year').format('LL')
							: t('analytics.cycles.details.values.startOfEmployment'),
				},
				{
					title: t('analytics.cycles.details.endDate'),
					description:
						cycleTemplate.interval === CycleTemplateInterval.Year
							? moment(endDate).endOf('year').format('LL')
							: t('analytics.cycles.details.values.doesNotEnd'),
				},
				{
					title: t('analytics.cycles.details.repeats'),
					description: cycleTemplate.repeats
						? t('analytics.cycles.details.values.repeats')
						: t('analytics.cycles.details.values.doesNotRepeat'),
				},
				...(successor
					? [
							{
								title: t('analytics.cycles.details.nextCycle'),
								description: successorTitle,
								onClick: isSameCycleSuccessor
									? undefined
									: () => onClickCycleTemplate(successor.cycleTemplateID),
							},
						]
					: []),
			]}
		/>
	);
}
